'use client';

import { useState } from 'react';

interface ImpersonationBannerProps {
  /** Email of the user currently being impersonated. */
  impersonatedEmail: string | null;
  /** Email of the admin behind the session. */
  adminEmail: string | null;
}

/**
 * Persistent warning shown while an admin is impersonating another user, with
 * the control to end the session.
 *
 * Rendered in normal document flow (sticky, not fixed) so it pushes the app
 * down rather than covering the top navigation, while staying visible on
 * scroll. Styling is inline to stay independent of the Figma-exported
 * stylesheets, which are only loaded on some routes.
 */
export default function ImpersonationBanner({
  impersonatedEmail,
  adminEmail,
}: ImpersonationBannerProps) {
  const [isEnding, setIsEnding] = useState(false);

  const endImpersonation = async () => {
    setIsEnding(true);
    try {
      const response = await fetch('/api/admin/impersonate', {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Failed to end impersonation (${response.status})`);
      }
      // Full reload rather than router.refresh(): the session is resolved
      // server-side from the cookie, so everything currently rendered must be
      // rebuilt as the admin.
      window.location.href = '/web/admin/users';
    } catch (error) {
      console.error('Failed to end impersonation:', error);
      setIsEnding(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 80000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        padding: '10px 16px',
        backgroundColor: '#B42318',
        color: '#fff',
        fontFamily:
          'var(--font-founders-grotesk-text), system-ui, sans-serif',
        fontSize: '14px',
        lineHeight: '18px',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
      }}
    >
      <span>
        Viewing as <strong>{impersonatedEmail ?? 'unknown user'}</strong>
        {adminEmail ? <> · signed in as {adminEmail}</> : null}
      </span>
      <button
        type="button"
        onClick={endImpersonation}
        disabled={isEnding}
        style={{
          padding: '4px 12px',
          borderRadius: '6px',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          backgroundColor: 'transparent',
          color: '#fff',
          fontFamily: 'inherit',
          fontSize: '14px',
          lineHeight: '18px',
          cursor: isEnding ? 'default' : 'pointer',
          opacity: isEnding ? 0.6 : 1,
        }}
      >
        {isEnding ? 'Ending…' : 'End impersonation'}
      </button>
    </div>
  );
}
